import Head from "next/head";
import nookies from "nookies";
import jwt_decode from "jwt-decode";
import styled from "styled-components";
import css from "@styled-system/css";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useRouter } from "next/router";
import {
  Box,
  LinkButton,
  Typography,
  OverlayProvider,
  Snackbar,
} from "@bca/ui-library";

import Layout from "../src/Layout";
import DegradedServicesBanner from "../src/DegradedServicesBanner/DegradedServicesBanner";
import ConditionalRender from "../src/ConditionalRender";
import GeneralErrorPage from "../src/GeneralErrorPage";
import AccountErrorPage from "../src/AccountErrorPage";
import ClaimsBottomSection from "../src/Claims/ClaimsBottomSection";
import getToken from "../sso/getToken";
import getEnvironment from "../src/getEnvironment";
import { redirectToIdp } from "../sso/redirectToIdp";
import FiltersDesktop from "../src/Filters/FiltersDesktop";
import DateRangeMobileContainer from "../src/Filters/DateRangeMobileContainer";
import {
  NUMBER_OF_DAYS_OPTIONS,
  PurchasesUrlQueryParams,
  SortOptionsTypes,
  SORT_OPTIONS,
} from "../src/APICalls/buildGetPurchasesUrl";
import FiltersMobile from "../src/Filters/FiltersMobile";
import {
  MAX_MOBILE_SCREEN,
  useViewport,
  Devices,
} from "../src/Hooks/useViewport.hook";
import VehiclesPagination from "../src/VehiclesPagination/VehiclesPagination";
import Sort from "../src/Sort/Sort";
import DecodedToken from "../src/Interfaces/DecodedToken";
import User from "../src/Interfaces/User";
import useAppliedCheckBoxes from "../src/Hooks/useAppliedCheckBoxes";
import useUpdateFiltersAndUrl from "../src/Hooks/useUpdateFiltersAndUrl";
import usePrevious from "../src/Hooks/usePrevious";
import VehicleGrid from "../src/VehicleGrid/VehicleGrid";
import NoResults from "../src/NoResults/NoResults";
import ClaimsPopover from "../src/Claims/ClaimsPopover";
import { useGetPurchases } from "../src/Hooks/useGetPurchases.hook";
import { useGetExportFlag } from "../src/Hooks/useGetExportFlag";
import { ADD_EXPORT_FLAG } from "../src/redux/slices/appSlice";
import MultipleDeliverySlideout from "../src/Slideout/MultipleDeliverySlideout";
import { storeType } from "../src/redux/store";
import { SHOW_SNACKBAR } from "../src/redux/slices/snackbarSlice";
import { MAX_SELECTED_VEHICLES } from "../src/constants";
import getListOfAppliedFilters from "../src/Filters/getListOfAppliedFilters";
import buildPageUrl from "../src/Filters/buildPageUrl";
import getBuyerAPIDomain from "../src/APICalls/getBuyerAPIDomain";
import { useAuth } from "@bca-auth0/bca-auth0-provider";
import { FeatureFlagManager } from "bca-feature-flag-provider";
import { GetServerSideProps } from "next";
import { useAuthZeroContext } from "../src/context/AuthZeroContext";

import "../src/lib/patchCrypto";

export const DESKTOP_PAGE_SIZE = 24;
const MOBILE_PAGE_SIZE = 12;
const VRM_QUERY_MINIMUM_LENGTH = 3;
export const DEFAULT_NUMBER_OF_DAYS_LABEL = "7 Days";
export const DEFAULT_NUMBER_OF_DAYS = "7";
export const DEFAULT_SORT_OPTION_LABEL = "Sale Date (New)";
const DEFAULT_SNACKBAR_DISPLAY_TIME = 5000;

export const DEFAULT_PURCHASES_PARAMETERS: PurchasesUrlQueryParams = {
  numberofDays: DEFAULT_NUMBER_OF_DAYS,
  pageSize: DESKTOP_PAGE_SIZE,
  page: 1,
  q: "",
  sort: "SaleDateDescending",
  additionalFilters: { bq: "Eligibility:Any|Payment:Any" },
};

export interface MyPurchasesProps {
  idToken: string;
  isToUseAuthZero: boolean;
  user?: User;
  authLogoutUrl?: string;
}

const Container = styled(Box)`
  width: 100%;
  margin-left: auto;
  margin-right: auto;
  ${css({
    maxWidth: ["100%", "100%", "100%", 1440],
    px: [5, 5, 60, 60],
  })}
`;

export interface VrmQueryState {
  q: string;
  triggeredBy: "ENTER" | "KEYPRESS" | "SUBMIT" | "LOAD";
}

const MyPurchases: React.FunctionComponent<MyPurchasesProps> = ({
  user: serverTokenUser,
  idToken: serverIdToken,
  isToUseAuthZero,
  authLogoutUrl,
}) => {
  const { setIsToUseAuthZero, setAuthZeroAccessToken } = useAuthZeroContext();

  const {
    accessToken: authAccessToken,
    isAuthenticated,
    authUser,
    error: authError,
    idToken: authIdToken,
  } = useAuth(!isToUseAuthZero);

  useEffect(() => {
    if (authError) {
      window.location.href = authLogoutUrl;
    }

    if (isAuthenticated && !authError) {
      setIsToUseAuthZero(isToUseAuthZero);
      setAuthZeroAccessToken(authAccessToken);
    }
  }, [
    isAuthenticated,
    authError,
    isToUseAuthZero,
    authAccessToken,
    setIsToUseAuthZero,
    setAuthZeroAccessToken,
    authLogoutUrl,
  ]);

  const user = isToUseAuthZero && isAuthenticated ? authUser : serverTokenUser;
  const idToken =
    isToUseAuthZero && isAuthenticated ? authIdToken : serverIdToken;

  const dispatch = useDispatch();
  const [isClientSide, setIsClientSide] = useState(false);
  const viewportWidth = useViewport();
  const isNotDesktop = viewportWidth !== Devices.DESKTOP;
  const [purchasesUrlQueryParams, setParams] =
    useState<PurchasesUrlQueryParams>(DEFAULT_PURCHASES_PARAMETERS);
  const [getOpenBankingLinkError, setGetOpenBankingLinkError] =
    useState<boolean>(false);
  const [dateRangeMobileVisible, setDateRangeMobileVisible] = useState(false);
  const showDateRangeMobile = () => setDateRangeMobileVisible(true);
  const hideDateRangeMobile = () => setDateRangeMobileVisible(false);
  const [desktopVrmQuery, setDesktopVrmQuery] = useState<VrmQueryState>({
    q: DEFAULT_PURCHASES_PARAMETERS.q,
    triggeredBy: "ENTER",
  });
  const prevQ = usePrevious(purchasesUrlQueryParams.q);

  const [initialLoad, setInitialLoad] = useState(true);
  const router = useRouter();

  const setPurchasesUrlQueryParams = (param, withRouter = true) => {
    setParams(param);
    if (withRouter) {
      const res = buildPageUrl(param);
      setDesktopVrmQuery({ q: param.q || "", triggeredBy: "LOAD" });
      router.push(res === "" ? "/" : res, undefined, { shallow: true });
    }
  };

  useEffect(() => {
    if (window.innerWidth < MAX_MOBILE_SCREEN) {
      setPurchasesUrlQueryParams({
        ...purchasesUrlQueryParams,
        pageSize: MOBILE_PAGE_SIZE,
      });
    }
    setIsClientSide(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const store = useSelector((state: storeType) => ({
    selectedForDelivery: state.bookDelivery.selectedForDelivery,
    snackbarState: state.snackbar.snackbarState,
  }));

  const showMultipleDeliverySlideout =
    isClientSide && Boolean(store.selectedForDelivery.length);

  const shouldShowSnackbar =
    store.selectedForDelivery.length >= MAX_SELECTED_VEHICLES;

  useEffect(() => {
    if (shouldShowSnackbar) {
      dispatch(SHOW_SNACKBAR(true));
      const timer = setTimeout(() => {
        dispatch(SHOW_SNACKBAR(false));
      }, DEFAULT_SNACKBAR_DISPLAY_TIME);
      return () => {
        clearTimeout(timer);
      };
    }
  }, [shouldShowSnackbar, dispatch]);

  const onPageChange = (event: any, page: number) => {
    setPurchasesUrlQueryParams({
      ...purchasesUrlQueryParams,
      page,
    });
    window.scrollTo(0, 0);
  };

  const setSelectedSort = (event) => {
    setPurchasesUrlQueryParams({
      ...purchasesUrlQueryParams,
      sort: event.target.value,
    });
  };

  const setPurchasesUrlParams = (
    purchasesUrlParams: PurchasesUrlQueryParams
  ) => {
    setPurchasesUrlQueryParams({
      ...purchasesUrlQueryParams,
      ...purchasesUrlParams,
      page: 1,
    });
    if ("q" in purchasesUrlParams) {
      setDesktopVrmQuery({
        q: purchasesUrlParams.q,
        triggeredBy: "SUBMIT",
      });
    }
  };

  useEffect(() => {
    if (
      initialLoad === false &&
      (desktopVrmQuery.q.length >= VRM_QUERY_MINIMUM_LENGTH ||
        desktopVrmQuery.q === DEFAULT_PURCHASES_PARAMETERS.q)
    ) {
      const refreshAndPushAnalytics = () => {
        setPurchasesUrlQueryParams({
          ...purchasesUrlQueryParams,
          q: desktopVrmQuery.q,
          sort:
            prevQ === "" && desktopVrmQuery.q !== ""
              ? SORT_OPTIONS.RegistrationNumberAscending
              : purchasesUrlQueryParams.sort,
          page: 1,
        });
        return;
      };
      if (desktopVrmQuery.triggeredBy === "ENTER") {
        refreshAndPushAnalytics();
        return;
      }
      const delayDebounceFn = setTimeout(() => {
        if (desktopVrmQuery.triggeredBy === "KEYPRESS") {
          refreshAndPushAnalytics();
        }
      }, 1000);

      return () => clearTimeout(delayDebounceFn);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [desktopVrmQuery]);

  const {
    appliedCheckboxes,
    setAppliedCheckboxes,
    setAppliedCheckboxesCheckBoxGroup,
    setAppliedCheckboxesCheckBoxGroupRemoveChip,
    setAppliedCheckboxesSingleSelect,
  } = useAppliedCheckBoxes();
  const { hasExportAccount } = useGetExportFlag(isToUseAuthZero);
  useEffect(() => {
    dispatch(ADD_EXPORT_FLAG(hasExportAccount));
  }, [hasExportAccount, dispatch]);

  const {
    filters,
    degradedServices,
    selectedFilterLabel,
    totalPages,
    totalResultCount,
    error,
    purchasesData,
    isLoading,
    sortOptions,
    selectedSortOption,
    selectedSortOptionLabel,
  } = useGetPurchases(
    purchasesUrlQueryParams,
    isClientSide,
    user,
    isToUseAuthZero,
    initialLoad,
    appliedCheckboxes
  );

  const finalDegradedServices = getOpenBankingLinkError
    ? [...degradedServices, "GetOpenBankingLinkError"]
    : degradedServices;

  const showGeneralErrorPage =
    (error && error.message !== "InvalidCasAccountNumber") ||
    degradedServices?.length > 1;

  const showAccountErrorPage =
    error && error.message === "InvalidCasAccountNumber";
  if (!isLoading && initialLoad === true) {
    setInitialLoad(false);
  }

  const showAuthErrorPage = Boolean(isToUseAuthZero && authError);

  const filtersContainerFlexDirection = isNotDesktop ? "column" : "row";
  const filtersContainerMarginTop = isNotDesktop ? "0" : "5";

  const isClearAllDisabled =
    purchasesUrlQueryParams.numberofDays ===
      DEFAULT_PURCHASES_PARAMETERS.numberofDays &&
    purchasesUrlQueryParams.q === DEFAULT_PURCHASES_PARAMETERS.q &&
    purchasesUrlQueryParams.sort === DEFAULT_PURCHASES_PARAMETERS.sort &&
    purchasesUrlQueryParams.additionalFilters.bq ===
      DEFAULT_PURCHASES_PARAMETERS.additionalFilters.bq &&
    purchasesUrlQueryParams.page === DEFAULT_PURCHASES_PARAMETERS.page;

  const onClearAll = () => {
    setPurchasesUrlQueryParams({
      ...purchasesUrlQueryParams,
      numberofDays: DEFAULT_PURCHASES_PARAMETERS.numberofDays,
      q: DEFAULT_PURCHASES_PARAMETERS.q,
      sort: DEFAULT_PURCHASES_PARAMETERS.sort,
      additionalFilters: DEFAULT_PURCHASES_PARAMETERS.additionalFilters,
      page: DEFAULT_PURCHASES_PARAMETERS.page,
    });
    setAppliedCheckboxes([
      {
        filterKey: "Any",
        categoryKey: "Eligibility",
        groupedBy: null,
        parentFilterKey: null,
      },
      {
        filterKey: "Any",
        categoryKey: "Payment",
        groupedBy: null,
        parentFilterKey: null,
      },
    ]);
  };

  const updatePurchasesUrlQueryParametersFromUrl = () => {
    setPurchasesUrlQueryParams(
      {
        ...purchasesUrlQueryParams,
        sort:
          (router.query.sort as SortOptionsTypes) ||
          purchasesUrlQueryParams.sort,
        numberofDays:
          (router.query.numberofDays as NUMBER_OF_DAYS_OPTIONS) ||
          purchasesUrlQueryParams.numberofDays,
        page:
          parseInt(router.query.page as string) || purchasesUrlQueryParams.page,
        q: (router.query.q as string) || purchasesUrlQueryParams.q,
        additionalFilters: {
          bq: router.query.bq || purchasesUrlQueryParams.additionalFilters.bq,
        },
      },
      false
    );
    if (router.query.q) {
      setDesktopVrmQuery({
        q: router.query.q as string,
        triggeredBy: "LOAD",
      });
    }
    if (router.query.bq) {
      let updatedStateFromUrl = getListOfAppliedFilters(router.query);
      setAppliedCheckboxes(updatedStateFromUrl);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(updatePurchasesUrlQueryParametersFromUrl, []);

  const updateAdditionalFilters = (additionalFilters) => {
    setPurchasesUrlQueryParams({
      ...purchasesUrlQueryParams,
      additionalFilters,
      page: 1,
    });
  };

  useUpdateFiltersAndUrl(appliedCheckboxes);

  if (dateRangeMobileVisible && isNotDesktop) {
    return (
      <DateRangeMobileContainer
        hideDateRangeMobile={hideDateRangeMobile}
        filters={filters}
        prevQ={prevQ}
        sortOptions={sortOptions}
        appliedCheckboxes={appliedCheckboxes}
        setAppliedCheckboxesCheckBoxGroup={setAppliedCheckboxesCheckBoxGroup}
        setAppliedCheckboxesCheckBoxGroupRemoveChip={
          setAppliedCheckboxesCheckBoxGroupRemoveChip
        }
        purchasesUrlParams={purchasesUrlQueryParams}
        setPurchasesUrlParams={setPurchasesUrlParams}
        onClearAll={onClearAll}
        setAppliedCheckboxesSingleSelect={setAppliedCheckboxesSingleSelect}
        isToUseAuthZero={isToUseAuthZero}
      />
    );
  }

  const shouldShowNoResults = !isLoading && purchasesData.length === 0;
  return (
    <OverlayProvider>
      <Head>
        <title>My Purchases</title>
      </Head>
      <Layout
        user={
          user
            ? { firstName: user.firstName, lastName: user.lastName }
            : undefined
        }
        idToken={idToken}
        isToUseAuthZero={isToUseAuthZero}
        isAuthenticated={isAuthenticated}
        logoutUrl={authLogoutUrl}
      >
        <Box as="main" display="flex" flexDirection="column">
          <Box as="section" backgroundColor="#fafafa">
            <Container py={5}>
              <LinkButton
                variant="tertiary"
                icon="arrow-left"
                iconWidth={20}
                iconHeight={20}
                fontSize={18}
                shouldOpenInNewWindow={false}
                margin={0}
                mb={6}
                padding={0}
                link={`${process.env.VIRTUAL_DOMAIN}mybca/dashboard`}
                fontWeight={100}
              >
                Back to MyBCA
              </LinkButton>
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
              >
                <Typography variant="h4" htmlTag="h1" m={0}>
                  Purchases
                </Typography>
                <ClaimsPopover />
              </Box>
            </Container>
          </Box>
          {Boolean(finalDegradedServices.length) && (
            <DegradedServicesBanner degradedServices={finalDegradedServices} />
          )}
          <ConditionalRender
            condition={showGeneralErrorPage}
            component={<GeneralErrorPage />}
          >
            <ConditionalRender
              condition={showAccountErrorPage || showAuthErrorPage}
              component={<AccountErrorPage />}
            >
              <Box
                display="flex"
                flexDirection="column"
                minHeight="calc(100vh - 710px)"
                justifyContent="space-between"
              >
                <Container
                  as="section"
                  display="flex"
                  justifyContent="space-between"
                  flexDirection={filtersContainerFlexDirection}
                  marginTop={filtersContainerMarginTop}
                  marginBottom={64}
                  minHeight={400}
                >
                  {isNotDesktop && (
                    <FiltersMobile
                      showDateRangeMobile={showDateRangeMobile}
                      selectedFilterLabel={selectedFilterLabel}
                      selectedSortOptionLabel={selectedSortOptionLabel}
                      isLoading={isLoading}
                      purchasesUrlQueryParams={purchasesUrlQueryParams}
                      setPurchasesUrlParams={setPurchasesUrlParams}
                      onClearAll={onClearAll}
                      updateAdditionalFilters={updateAdditionalFilters}
                      appliedCheckboxes={appliedCheckboxes}
                      setAppliedCheckboxes={setAppliedCheckboxes}
                      isClearAllDisabled={isClearAllDisabled}
                      setAppliedCheckboxesSingleSelect={
                        setAppliedCheckboxesSingleSelect
                      }
                    />
                  )}
                  {!isNotDesktop && (
                    <FiltersDesktop
                      purchasesUrlQueryParams={purchasesUrlQueryParams}
                      setPurchasesUrlQueryParams={setPurchasesUrlQueryParams}
                      isLoading={isLoading}
                      filters={filters}
                      desktopVrmQuery={desktopVrmQuery}
                      setDesktopVrmQuery={setDesktopVrmQuery}
                      onClearAll={onClearAll}
                      isClearAllDisabled={isClearAllDisabled}
                      appliedCheckboxes={appliedCheckboxes}
                      setAppliedCheckboxesCheckBoxGroup={
                        setAppliedCheckboxesCheckBoxGroup
                      }
                      setAppliedCheckboxesCheckBoxGroupRemoveChip={
                        setAppliedCheckboxesCheckBoxGroupRemoveChip
                      }
                      updateAdditionalFilters={updateAdditionalFilters}
                      setAppliedCheckboxesSingleSelect={
                        setAppliedCheckboxesSingleSelect
                      }
                      isToUseAuthZero={isToUseAuthZero}
                    />
                  )}
                  <Box
                    width="100%"
                    maxWidth={985}
                    display="flex"
                    flexDirection="column"
                  >
                    {!isNotDesktop && (
                      <Sort
                        isLoading={isLoading}
                        onChange={setSelectedSort}
                        selectedSortOption={selectedSortOption}
                        sortOptions={sortOptions}
                      />
                    )}
                    {shouldShowNoResults && (
                      <NoResults
                        selectedFilterLabel={selectedFilterLabel}
                        appliedCheckboxes={appliedCheckboxes}
                        purchasesUrlQueryParams={purchasesUrlQueryParams}
                      />
                    )}
                    {!shouldShowNoResults && (
                      <VehicleGrid
                        isLoading={isLoading}
                        vehicles={purchasesData}
                        degradedServices={degradedServices}
                        setGetOpenBankingLinkError={setGetOpenBankingLinkError}
                        viewportWidth={viewportWidth}
                      />
                    )}
                    {showMultipleDeliverySlideout && (
                      <>
                        {store.snackbarState && (
                          <Snackbar
                            title="You've reached selection limit."
                            variant="warning"
                            verticalPosition="bottom"
                            paddingX={{ desktop: "60px", mobile: "20px" }}
                          />
                        )}
                        <MultipleDeliverySlideout />
                      </>
                    )}
                    <VehiclesPagination
                      totalResultCount={totalResultCount}
                      totalPages={totalPages}
                      page={purchasesUrlQueryParams.page}
                      onPageChange={onPageChange}
                      pageSize={purchasesUrlQueryParams.pageSize}
                      isLoading={isLoading}
                    />
                  </Box>
                </Container>
                <Box>
                  <Container mb={56}>
                    <ClaimsBottomSection />
                  </Container>
                  <div className="feedback-embedded" />
                </Box>
              </Box>
            </ConditionalRender>
          </ConditionalRender>
        </Box>
      </Layout>
    </OverlayProvider>
  );
};

export const getServerSideProps: GetServerSideProps = async (context) => {
  const featureFlagManager = new FeatureFlagManager();
  await featureFlagManager.initializeFeatureManager(
    process.env.APP_CONFIGURATION_URL
  );
  const useAuthZeroResult =
    await featureFlagManager.getFeatureFlagStatus("authzero");

  const isToUseAuthZero =
    !useAuthZeroResult.isError && useAuthZeroResult.isEnabled;

  if (isToUseAuthZero) {
    let props: any = {};
    props.appConfigurationUrl = process.env.APP_CONFIGURATION_URL;
    props.authProviderDomain = process.env.AUTH0_DOMAIN;
    props.authProviderClientId = process.env.AUTH0_CLIENT_ID;
    props.redirectUri = process.env.AUTH_PROVIDER_REDIRECT_URI;
    props.isToUseAuthZero = isToUseAuthZero;
    const buyerAPIDomain = getBuyerAPIDomain(isToUseAuthZero);
    props.buyerAPIDomain = buyerAPIDomain ? buyerAPIDomain.href : "";
    props.authLogoutUrl = process.env.AUTH0_LOGOUT_URL;

    return {
      props,
    };
  } else {
    // in prod redirecting to sso
    redirectToIdp(
      context,
      getEnvironment(),
      process.env.VIRTUAL_DOMAIN,
      process.env.URL_SUFFIX
    );

    let firstName = undefined;
    let lastName = undefined;
    let peep_analyticsid = "";
    let ssoId = "";
    let casId = "";

    const token = getToken(nookies.get(context));
    const idToken = nookies.get(context).idtoken || "";

    if (token) {
      const decodedToken = jwt_decode(token) as DecodedToken;
      firstName = decodedToken.user_name.split(" ")[0];
      lastName = decodedToken.user_name.split(" ")[1];
      peep_analyticsid = decodedToken.peep_analyticsid;
      ssoId = decodedToken.sub;
      casId = decodedToken.sourceaccount_id;
    }

    const props: MyPurchasesProps = {
      user: { firstName, lastName, peep_analyticsid, ssoId, casId },
      idToken,
      isToUseAuthZero,
    };

    return {
      props,
    };
  }
};

export default MyPurchases;
